/**
 * The one announcement to show this person right now, and how to
 * dismiss it.
 *
 * Lives in its own hook so the modal and the banner cannot both decide
 * they are the one rendering — the host picks a presentation, and only
 * one component ever mounts.
 *
 * ── When it fires ─────────────────────────────────────────────────────
 * Once per sign-in session, shortly after the first authenticated page
 * settles. In practice that is immediately after signing in, because
 * that is when a session starts and the landing page is where people
 * arrive — but it deliberately is NOT wired to the login event itself.
 *
 * Wiring it to login would miss the case that matters most: someone
 * whose token is still valid from last week never logs in again, so they
 * would never see anything we shipped in between. Keying on the session
 * catches them on their next visit instead.
 *
 * The session guard is what stops it being a nuisance. Without it the
 * modal reappears on every route change and every reload until the
 * acknowledgement write lands — so a backend hiccup turns an
 * announcement into something that repeatedly interrupts your work.
 *
 * ── Several at once ───────────────────────────────────────────────────
 * Someone back from a week away may have missed more than one. They see
 * all of them, but as ONE interruption: a single surface paged "1 of 3"
 * rather than three popups in a row. Three separate popups is how people
 * learn to close them without reading.
 *
 * Each page is acknowledged as it is passed, so `via` still records what
 * they did with each one. Closing at 1 of 3 leaves the other two unseen
 * — they come back next sign-in rather than being quietly counted as
 * read, because they were not.
 *
 * The run can be walked backwards as well as forwards. Someone who
 * clicks past something and wants a second look should not have to wait
 * until tomorrow for it to come round again — and since it was already
 * acknowledged on the way past, it will not.
 */

import { useEffect, useRef, useState } from 'react';

import {
  useAcknowledgeAnnouncement,
  useAnnouncementAcks,
  useLiveAnnouncements,
  type AckVia,
  type Announcement,
  type Presentation,
} from '../../api/announcements';
import { useAuth } from '../../auth/useAuth';
import {
  markAnnouncementSurfaced,
  pickAnnouncements,
  readSurfacedAnnouncement,
} from '../../data/announcements';

/**
 * Let the page land before interrupting it. Appearing in the same frame
 * as the content reads like a glitch; a beat later reads deliberate.
 */
const SETTLE_MS = 700;

export interface CurrentAnnouncement {
  /** The one on screen, or null when there is nothing to show. */
  announcement: Announcement | null;
  /** 1-based position in the run, for the "1 of 3" counter. */
  index: number;
  /** How many are unseen in total. 1 hides the counter entirely. */
  total: number;
  /**
   * How the whole run is presented. The strongest presentation among the
   * unseen wins: if an admin marked ANY of them as a popup they judged it
   * worth stopping someone for, and demoting that to a banner because a
   * newer announcement happened to be quiet would override the one
   * decision the admin screen exists to give them.
   */
  presentation: Presentation;
  /**
   * Acknowledge the one on screen and move to the next, or close if it
   * was the last. `via` records what they did with THIS one.
   */
  advance: (via: AckVia) => void;
  /**
   * Acknowledge the one on screen and close the whole run. Anything
   * behind it stays unseen.
   */
  close: (via: AckVia) => void;
  /**
   * Step back to the one before, to re-read it.
   *
   * Deliberately does NOT acknowledge anything: going back is
   * revisiting, not passing. The page you came from was already
   * acknowledged on the way forward, and re-acknowledging would be a
   * no-op anyway — the write keeps the first interaction.
   */
  goBack: () => void;
  /** False on the first page, where there is nothing behind. */
  canGoBack: boolean;
}

export function useCurrentAnnouncement(): CurrentAnnouncement {
  const { user } = useAuth();
  const live = useLiveAnnouncements(!!user);
  const acks = useAnnouncementAcks(!!user);
  const acknowledge = useAcknowledgeAnnouncement();
  const [visible, setVisible] = useState(false);
  const [cursor, setCursor] = useState(0);

  // Snapshotted once. Reading sessionStorage during render would flip to
  // "already surfaced" the instant we write it, closing the modal in the
  // same breath as opening it.
  //
  // A boolean, not the key: what matters is whether this session has
  // already been interrupted, not by which announcement.
  const spentThisSession = useRef<boolean | null>(null);
  if (spentThisSession.current === null) {
    spentThisSession.current = readSurfacedAnnouncement() !== '';
  }

  // Wait for BOTH lists before showing anything. Rendering optimistically
  // and retracting would flash an announcement at someone who dismissed
  // it last week — the one thing this must not do. Either call failing
  // means we show nothing, which is the right failure for a decoration.
  const ready = !!user && !live.isError && !acks.isError && !!live.data && !!acks.data;

  // The whole run, captured ONCE when it first becomes available.
  // Recomputing it would be self-destroying: acknowledging page 1
  // removes it from the list, which would renumber "2 of 3" into
  // "1 of 2" under the reader mid-sentence.
  const run = useRef<Announcement[] | null>(null);
  if (run.current === null && ready) {
    run.current = pickAnnouncements(live.data!.items, acks.data!.acknowledged);
  }
  const queue = run.current ?? [];
  const candidate = queue[cursor] ?? null;
  const candidateKey = queue[0]?.key ?? null;

  useEffect(() => {
    if (!candidateKey || spentThisSession.current) {
      setVisible(false);
      return;
    }
    const timer = window.setTimeout(() => {
      spentThisSession.current = true;
      markAnnouncementSurfaced(candidateKey);
      setVisible(true);
    }, SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [candidateKey]);

  const ackCurrent = (via: AckVia) => {
    if (candidate) acknowledge.mutate({ key: candidate.key, via });
  };

  return {
    announcement: visible && candidate ? candidate : null,
    index: cursor + 1,
    total: queue.length,
    presentation: queue.some((a) => a.presentation === 'modal') ? 'modal' : 'banner',
    advance: (via: AckVia) => {
      ackCurrent(via);
      if (cursor + 1 < queue.length) setCursor(cursor + 1);
      else setVisible(false);
    },
    close: (via: AckVia) => {
      ackCurrent(via);
      setVisible(false);
    },
    goBack: () => setCursor((c) => Math.max(0, c - 1)),
    canGoBack: cursor > 0,
  };
}
